import React from 'react';
import { Box, Paper, Stack, Typography, Chip, Divider, useTheme, alpha } from '@mui/material';
import {
    WorkOutline as WorkIcon,
    SchoolOutlined as SchoolIcon,
    BusinessOutlined as BusinessIcon,
} from '@mui/icons-material';
import { Worker, WorkExperience as WorkExperienceEntry, EducationEntry } from '../../models/worker';

/** Read-only display of a worker's structured work experience + education. */
const WorkerExperience: React.FC<{ worker: Worker }> = ({ worker }) => {
    const theme = useTheme();
    const experiences = worker.workExperiences ?? [];
    const education = worker.education ?? [];

    const fmtRange = (start?: string, end?: string, current?: boolean) => {
        const s = (start || '').trim();
        const e = current ? 'heden' : (end || '').trim();
        if (!s && !e) return '';
        return [s || '?', e || '?'].join(' — ');
    };

    const emptyState = (label: string) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {label}
        </Typography>
    );

    return (
        <Stack spacing={2}>
            {/* Work experience */}
            <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <WorkIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1" fontWeight={800}>
                        Work experience
                    </Typography>
                    <Chip size="small" label={experiences.length} sx={{ height: 20, fontWeight: 700 }} />
                </Stack>

                {experiences.length === 0 ? (
                    emptyState('No work experience recorded yet.')
                ) : (
                    <Stack spacing={1.5}>
                        {experiences.map((exp: WorkExperienceEntry, i) => (
                            <Paper
                                key={i}
                                variant="outlined"
                                sx={{ p: 1.75, borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.8) }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} flexWrap="wrap">
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={700}>
                                            {exp.position || 'Unknown position'}
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
                                            <BusinessIcon sx={{ fontSize: 15 }} color="action" />
                                            <Typography variant="caption" color="text.secondary">
                                                {exp.companyName || '—'}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                    <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                                        {exp.isCurrentJob && (
                                            <Chip size="small" color="success" label="Current" sx={{ height: 20, fontSize: '0.68rem' }} />
                                        )}
                                        {exp.workType && (
                                            <Chip size="small" variant="outlined" label={exp.workType} sx={{ height: 20, fontSize: '0.68rem' }} />
                                        )}
                                    </Stack>
                                </Stack>
                                {fmtRange(exp.startDate, exp.endDate, exp.isCurrentJob) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {fmtRange(exp.startDate, exp.endDate, exp.isCurrentJob)}
                                    </Typography>
                                )}
                                {exp.responsibilities && (
                                    <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                                        {exp.responsibilities}
                                    </Typography>
                                )}
                            </Paper>
                        ))}
                    </Stack>
                )}
            </Box>

            <Divider />

            {/* Education */}
            <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <SchoolIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1" fontWeight={800}>
                        Education
                    </Typography>
                    <Chip size="small" label={education.length} sx={{ height: 20, fontWeight: 700 }} />
                </Stack>

                {education.length === 0 ? (
                    emptyState('No education recorded yet.')
                ) : (
                    <Stack spacing={1.5}>
                        {education.map((ed: EducationEntry, i) => (
                            <Paper
                                key={i}
                                variant="outlined"
                                sx={{ p: 1.75, borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.8) }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} flexWrap="wrap">
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={700}>
                                            {ed.courseName || ed.educationType || 'Education'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                            {ed.institutionName || '—'}
                                            {ed.level ? ` · ${ed.level}` : ''}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                                        {ed.diplomaReceived && (
                                            <Chip size="small" color="success" label="Diploma" sx={{ height: 20, fontSize: '0.68rem' }} />
                                        )}
                                        {!ed.diplomaReceived && ed.completed && (
                                            <Chip size="small" variant="outlined" label="Completed" sx={{ height: 20, fontSize: '0.68rem' }} />
                                        )}
                                    </Stack>
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {[ed.startYear, ed.endYear].filter(Boolean).join(' — ')}
                                </Typography>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </Box>
        </Stack>
    );
};

export default WorkerExperience;
